import { Source, Sources } from '../../lib/models/Chat';

export const BRACKET_GROUP_REGEX = /\[([^\]\n]+)\]/g;
const MAX_FALLBACK_CITATION_NUMBER = 50;
const MAX_CITATION_RANGE_SPAN = 20;

export function toSourceList(sources?: Sources | Source[] | null): Source[] {
  if (!sources) return [];
  return Array.isArray(sources) ? sources : Object.values(sources);
}

function readSourceNumber(source: Source, fallback?: unknown): number | null {
  return (
    parseSourceNumber((source as any).source_number ?? (source as any).sourceNumber) ??
    parseSourceNumber(fallback)
  );
}

/** Map citation numbers ([1], [2], …) to source records from any API/history shape. */
export function buildSourceByNumberMap(sources?: Sources | Source[] | null): Map<number, Source> {
  const sourceMap = new Map<number, Source>();
  if (!sources) return sourceMap;

  const assign = (sourceNumber: number | null, source: Source) => {
    if (sourceNumber && !sourceMap.has(sourceNumber)) {
      sourceMap.set(sourceNumber, source);
    }
  };

  if (Array.isArray(sources)) {
    sources.forEach((source, index) => {
      assign(readSourceNumber(source, index + 1), source);
    });
    return sourceMap;
  }

  Object.entries(sources).forEach(([key, source]) => {
    assign(readSourceNumber(source, key), source);
  });

  return sourceMap;
}

export function parseSourceNumber(value: unknown): number | null {
  const num = typeof value === 'string' ? parseInt(value, 10) : Number(value);
  return Number.isInteger(num) && num > 0 ? num : null;
}

export function parseCitationGroup(
  bracketContent: string,
  knownSourceNumbers: Set<number>,
): number[] | null {
  const normalized = bracketContent.trim();
  if (!normalized) return null;

  // Ignore non-citation tags such as [perception_tool_call]
  if (!/^[\d,\s-]+$/.test(normalized)) return null;

  const tokens = normalized
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);
  if (tokens.length === 0) return null;

  const parsed: number[] = [];
  for (const token of tokens) {
    const singleMatch = token.match(/^(\d+)$/);
    if (singleMatch) {
      const num = parseInt(singleMatch[1], 10);
      if (!Number.isInteger(num) || num <= 0) return null;
      parsed.push(num);
      continue;
    }

    const rangeMatch = token.match(/^(\d+)\s*-\s*(\d+)$/);
    if (!rangeMatch) return null;

    const start = parseInt(rangeMatch[1], 10);
    const end = parseInt(rangeMatch[2], 10);
    if (!Number.isInteger(start) || !Number.isInteger(end) || start <= 0 || end < start) return null;
    if (end - start > MAX_CITATION_RANGE_SPAN) return null;

    for (let value = start; value <= end; value += 1) {
      parsed.push(value);
    }
  }

  const uniqueNumbers = Array.from(new Set(parsed));
  if (knownSourceNumbers.size > 0) {
    return uniqueNumbers.some((num) => knownSourceNumbers.has(num)) ? uniqueNumbers : null;
  }

  const fallbackSafeNumbers = uniqueNumbers.filter((num) => num <= MAX_FALLBACK_CITATION_NUMBER);
  return fallbackSafeNumbers.length > 0 ? fallbackSafeNumbers : null;
}

export function processCitationsForMarkdown(content: string, knownSourceNumbers: Set<number>): string {
  // guarantee force merge of the citations into a combined block
  const merged = mergeAdjacentCitationBrackets(content || '');
  return merged.replace(
    BRACKET_GROUP_REGEX,
    (match, bracketContent: string, offset: number, fullText: string) => {
      if (offset > 0 && fullText[offset - 1] === '\\') return match;
      const citations = parseCitationGroup(bracketContent, knownSourceNumbers);
      if (!citations) return match;
      return `<cite data-numbers="${citations.join(',')}"></cite>`;
    }
  );
}

export function mergeAdjacentCitationBrackets(content: string): string {
  // merge together adjacent numeric citation brackets, e.g. [1][2] -> [1, 2] or [1, 2][3] -> [1, 2, 3]
  const normalized = content.replace(/\[([\d,\s-]+)\[/g, '[$1]');
  return normalized.replace(
    /(\[[\d,\s-]+\])(\s*\[[\d,\s-]+\])+/g,
    (match) => {
      const numbers = match.match(/\d+/g);
      if (!numbers) return match;
      const unique = Array.from(new Set(numbers.map(Number))).sort((a, b) => a - b);
      return `[${unique.join(', ')}]`;
    }
  );
}
